import FontIcon from 'components/icons/FontIcon';
import { IconButton } from 'components/v2/buttons';
import CompilerSelectControlledComponent from 'components/v2/epd-compiler-inputs/CompilerSelectControlledComponent';
import WizardAddButton from 'components/v2/epd-wizard-inputs/WizardAddButton';
import { HelpBox } from 'components/v2/help-boxes';
import TooltipHelpIcon from 'components/v2/icons/TooltipHelpIcon';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateEpdDataSource, useDeleteEpdDataSource, useUpdateEpdDataSource } from 'services/api/mutations';
import { useEpdDataSourceOptions, useEpdDataSources, useEPDDefinitionInformation } from 'services/api/queries';
import styled from 'styled-components';
import { CheckBoxPanel, FieldPanel, FieldPanelHeader, FieldPanelInput } from 'styles/v2/Styles.styled';
import { DataSourceOptionModel, EPDDataSourceModel, Option } from 'types/types';
import { getValidationIssueMessage } from 'util/compilerValidation';

import useIsReadOnlyMode from '../../hooks/useIsReadOnlyMode';
import WizardCheckbox from 'components/v2/epd-wizard-inputs/WizardCheckbox';
import WizardTextAreaComponent from 'components/v2/epd-wizard-inputs/WizardTextArea';
import { ErrorAlertPanel } from '../../panels/Styles.styled';

const DataSourceSection: React.FunctionComponent<{
  epdVersionId: string;
  errors?: any;
  onChangeEPDDefinitionProperty: (propertyName: string, value: any) => void;
}> = ({ epdVersionId, errors, onChangeEPDDefinitionProperty }) => {
  const { t } = useTranslation();
  const dataSources = useEpdDataSources(epdVersionId!).data;
  const dataSourceOptionsSource = useEpdDataSourceOptions().data;
  const createDataSource = useCreateEpdDataSource(epdVersionId);
  const updateDataSource = useUpdateEpdDataSource(epdVersionId);
  const deleteDataSource = useDeleteEpdDataSource(epdVersionId);
  const isReadOnly = useIsReadOnlyMode();
  const epdDefinitionInformation = useEPDDefinitionInformation(epdVersionId).data;

  const dataSourceErrors = (index: number) => {
    return errors?.[`epdDataSource[${index}]`];
  };

  const dataSourceOptions = dataSourceOptionsSource
    ?.filter((x) => !x.isDisabled)
    .filter((x) => x.dataSourceVersions && x.dataSourceVersions.some((v) => !v.isDisabled))
    .map((x: DataSourceOptionModel) => {
      return {
        value: x.id,
        label: x.name,
      } as Option;
    });

  const getDataSourceVersionOptions = (dataSourceId?: string) => {
    if (!dataSourceId) {
      return [] as Option[];
    }

    const dataSource = dataSourceOptionsSource?.find(
      (x: DataSourceOptionModel) => x.id.toLowerCase() === dataSourceId.toLowerCase()
    );

    return dataSource?.dataSourceVersions;
  };

  const onChangeDataSource = async (id: string | undefined, propertyName: string, val: any) => {
    let newData;
    if (id) {
      const existingItem = dataSources?.find((x) => x.id === id);
      newData = { ...existingItem, [propertyName]: val } as EPDDataSourceModel;
      if (propertyName === 'dataSourceId') {
        newData.dataSourceVersionId = undefined;
      }
      updateDataSource.mutate(newData);
    } else {
      newData = { epdVersionId: epdVersionId, [propertyName]: val } as EPDDataSourceModel;
      createDataSource.mutate(newData);
    }
  };

  const handleAddNewDataSource = () => {
    const newData = { epdVersionId: epdVersionId } as EPDDataSourceModel;
    createDataSource.mutate(newData);
  };

  const handleDeleteDataSource = (id: string) => {
    deleteDataSource.mutate(id);
  };

  return (
    <FieldPanel>
      <FieldPanelHeader className="w-full">
        {t(`epdCompiler.step3.lcaSpecificationTab.properties.dataSource.title`)}{' '}
        <TooltipHelpIcon content={t(`epdCompiler.step3.lcaSpecificationTab.properties.dataSource.tooltip`)} />
      </FieldPanelHeader>

      {errors?.datasourceSectionErrorDatabaseVersion && (
        <ErrorAlertPanel className="w-full">
          {getValidationIssueMessage(errors.datasourceSectionErrorDatabaseVersion)}
        </ErrorAlertPanel>
      )}

      {dataSources && dataSources.length > 0 ? (
        dataSources.map((item: EPDDataSourceModel, index: number) => {
          const dataSource = dataSourceOptionsSource?.find(
            (x: DataSourceOptionModel) => x.id.toLowerCase() === item.dataSourceId?.toLowerCase()
          );

          const dataSourceVersionOptions = getDataSourceVersionOptions(item.dataSourceId);
          const dataSourceVersion = dataSourceVersionOptions?.find(
            (x: Option) => x.value.toLowerCase() === item?.dataSourceVersionId?.toLowerCase()
          );
          return (
            <React.Fragment key={index}>
              <FieldPanelInput>
                <CompilerSelectControlledComponent
                  label={t(`epdCompiler.step3.lcaSpecificationTab.properties.dataSource.dataSource`)}
                  name="dataSourceId"
                  options={dataSourceOptions}
                  required
                  value={dataSource ? ({ value: dataSource.id, label: dataSource.name } as Option) : null}
                  onChanged={(propertyName: string, val: any) => {
                    onChangeDataSource(item.id, propertyName, val);
                  }}
                  placeholder="Select"
                  isClearable={false}
                  error={dataSourceErrors(index)?.database}
                />
                <CheckBoxGroup>
                  <WizardCheckbox
                    inputId={item.id}
                    label={t(`epdCompiler.step3.lcaSpecificationTab.properties.dataSource.isMain`)}
                    name="isMain"
                    value={item?.isMain}
                    onChanged={(name: string, value: any) => {
                      onChangeDataSource(item.id, name, value);
                    }}
                    disabled={isReadOnly}
                    size="small"
                  />
                </CheckBoxGroup>
              </FieldPanelInput>
              <SelectWithIconPanel>
                <CompilerSelectControlledComponent
                  label={t(`epdCompiler.step3.lcaSpecificationTab.properties.dataSource.dataSourceVersion`)}
                  name="dataSourceVersionId"
                  options={dataSourceVersionOptions?.filter((x: Option) => !x.isDisabled) ?? []}
                  required
                  value={dataSourceVersion ?? null}
                  onChanged={(propertyName: string, val: any) => {
                    onChangeDataSource(item.id, propertyName, val);
                  }}
                  placeholder="Select"
                  isClearable={false}
                  error={dataSourceErrors(index)?.databaseVersion || dataSourceErrors(index)?.database}
                />
                {!isReadOnly && dataSources.length > 1 && !item.isMain && (
                  <DeleteDataSourceIconButton
                    icon={<FontIcon icon="delete" />}
                    text
                    onClick={() => handleDeleteDataSource(item.id)}
                  />
                )}
              </SelectWithIconPanel>
            </React.Fragment>
          );
        })
      ) : (
        <>
          <FieldPanelInput>
            <CompilerSelectControlledComponent
              label={t(`epdCompiler.step3.lcaSpecificationTab.properties.dataSource.dataSource`)}
              name="dataSourceId"
              options={dataSourceOptions}
              required
              onChanged={(propertyName: string, val: any) => {
                onChangeDataSource(undefined, propertyName, val);
              }}
              placeholder="Select"
              isClearable={false}
            />
            <CheckBoxGroup>
              <WizardCheckbox
                inputId="isMain"
                label={t(`epdCompiler.step3.lcaSpecificationTab.properties.dataSource.isMain`)}
                name="isMain"
                value={false}
                onChanged={(name: string, value: any) => {
                  onChangeDataSource(undefined, name, value);
                }}
                disabled={isReadOnly}
                size="small"
              />
            </CheckBoxGroup>
          </FieldPanelInput>
          <CompilerSelectControlledComponent
            label={t(`epdCompiler.step3.lcaSpecificationTab.properties.dataSource.dataSourceVersion`)}
            name="dataSourceVersionId"
            options={[]}
            required
            onChanged={(propertyName: string, val: any) => {
              onChangeDataSource(undefined, propertyName, val);
            }}
            placeholder="Select"
            isClearable={false}
            containerStyle={{ alignSelf: 'flex-start' }}
          />
        </>
      )}
      <div className="w-full">
        <HelpBox>
          <span>{t(`epdCompiler.step3.lcaSpecificationTab.properties.dataSource.helpBoxNewField`)}</span>
        </HelpBox>
        <WizardAddButton
          label={t('epdCompiler.step3.lcaSpecificationTab.properties.dataSource.addNew')}
          onClick={handleAddNewDataSource}
        />

        <OtherContainer className="w-full">
          <CheckBoxPanel style={{ marginTop: 0 }}>
            <WizardCheckbox
              label="Other database"
              name="isOtherDatabaseUsedAsDatasource"
              value={epdDefinitionInformation?.isOtherDatabaseUsedAsDatasource ?? false}
              onChanged={onChangeEPDDefinitionProperty}
              disabled={isReadOnly}
            />
          </CheckBoxPanel>
          {epdDefinitionInformation?.isOtherDatabaseUsedAsDatasource && (
            <WizardTextAreaComponent
              containerClassName="w-full"
              label="Other database description"
              placeholder="Type here"
              name="otherDatabaseUsedAsDatasourceDescription"
              value={epdDefinitionInformation?.otherDatabaseUsedAsDatasourceDescription ?? ''}
              onChanged={onChangeEPDDefinitionProperty}
              error={errors?.otherDatabaseUsedAsDatasourceDescription}
            />
          )}
        </OtherContainer>
      </div>
    </FieldPanel>
  );
};

const CheckBoxGroup = styled.div`
  text-align: start;
  margin-top: 0.5rem;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  border-radius: 2px;
`;

const OtherContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  margin-top: 30px;
  row-gap: 16px;

  & .p-checkbox {
    width: 16px;
    height: 16px;
  }

  & .p-checkbox .p-checkbox-box {
    width: 14px;
    height: 14px;
    align-self: center;
  }
`;

const SelectWithIconPanel = styled.div`
  align-self: flex-start;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const DeleteDataSourceIconButton = styled(IconButton)`
  align-self: end;
  margin-left: 10px;
  margin-bottom: 10px;

  &.p-button.p-button-text {
    color: ${(props) => props.theme.colors.primaryBlack};
    & > i {
      font-size: 16px;
    }
  }
  &.p-button.p-button-text:hover {
    background: transparent;
  }
  &.p-disabled {
    opacity: 0.6;
  }
`;

export default DataSourceSection;
